import React, { PureComponent } from "react";

import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import paginationFactory from "react-bootstrap-table2-paginator";
import filterFactory, { textFilter } from "react-bootstrap-table2-filter";
import moment from "moment";

import Uri from "jsuri";

import {
  sortNumeric,
  selectFormat,
  TableContentCell,
  getMetricForColumn,
  Error,
} from "./../../Utils/Utils.js";

class FacebookReview extends PureComponent {
  componentDidMount() {
    this.props.childRef(this);
  }

  renderTable(data) {
    const { cells, title, url } = this.props;

    // add unique ids
    data.forEach((d, i) => (d.id = i));

    const columns = [
      {
        dataField: "title",
        text: "Title",
        headerClasses: "sticky-column",
        classes: "sticky-column",
        style: { width: "320px", minWidth: "320px", textAlign: "left" },
        headerAlign: "left",
        formatter: TableContentCell,
        formatExtraData: {
          name: new Uri(url).hasQueryParam("p")
            ? new Uri(url).getQueryParamValue("p")
            : url.split("/")[2],
          showDate: false,
        },
        csvFormatter: (cell, row) => row && (row.title || row.tweet_text || ""),
        sort: true,
        filter: textFilter({
          getFilter: (filter) => filter,
          placeholder: " ",
          className: "custom-table-filter-input",
          caseSensitive: false,
        }),
      },
      {
        dataField: "network",
        text: "Network",
        style: { width: "100px", minWidth: "100px" },
        csvFormatter: (cell) => cell.charAt(0).toUpperCase() + cell.slice(1),
        formatter: (cell) => cell.charAt(0).toUpperCase() + cell.slice(1),
        headerAlign: "left",
        sort: true,
        csvExport: true,
      },
      {
        dataField: "published_date",
        text: "Post Date",
        style: { width: "100px", minWidth: "100px" },
        csvFormatter: (cell, row) =>
          row && moment(row.published_date).format("YYYY-MM-DD"),
        headerAlign: "left",
        sort: true,
        csvExport: true,
        formatter: (cell, row) => {
          return moment(row.published_date).format("YYYY-MM-DD");
        },
        sortFunc: (a, b, order) => {
          if (order === "desc") {
            return new Date(b) - new Date(a);
          } else {
            return new Date(a) - new Date(b);
          }
        },
      },
      {
        dataField: "date_pub",
        text: "Date Pub",
        style: { width: "100px", minWidth: "100px" },
        csvFormatter: (cell, row) =>
          row && moment(row.published_date).format("YYYY-MM-DD"),
        headerAlign: "left",
        sort: true,
        csvExport: true,
        formatter: (cell, row) => {
          return moment(row.published_date).format("YYYY-MM-DD");
        },
        sortFunc: (a, b, order) => {
          if (order === "desc") {
            return new Date(b) - new Date(a);
          } else {
            return new Date(a) - new Date(b);
          }
        },
      },
      {
        dataField: "type",
        text: "Type",
        style: { width: "60px", minWidth: "60px", textTransform: "capitalize" },
        csvFormatter: (cell) => cell.charAt(0).toUpperCase() + cell.slice(1),
        formatter: (cell) => cell,
        sort: true,
      },
      {
        dataField: "username",
        text: "Handle",
        style: { width: "90px", minWidth: "90px" },
        csvFormatter: (cell) => cell,
        formatter: (cell) => cell,
        sort: true,
      },
      {
        dataField: "impressions",
        text: "Impressions",
        style: { width: "100px", minWidth: "100px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "reach",
        text: "Reach",
        style: { width: "90px", minWidth: "90px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "engagement_rate",
        text: "Engagement Rate",
        style: { width: "130px", minWidth: "130px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "percentage" },
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0.000%"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "link_clicks",
        text: "Link Clicks",
        style: { width: "120px", minWidth: "120px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "video_views",
        text: "Video Views",
        style: { width: "120px", minWidth: "120px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "video_view_rate",
        text: "Video View Rate",
        style: { width: "120px", minWidth: "120px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "percentage" },
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0.000%"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "video_views_10s",
        text: "Video Views 10s",
        style: { width: "120px", minWidth: "120px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "completion",
        text: "Completion Rate",
        style: { width: "120px", minWidth: "120px" },
        formatter: (cell) => selectFormat("percentage", cell && cell.value),
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "%"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "avg_watch_time",
        text: "Minutes Per Video View",
        style: { width: "150px", minWidth: "150px" },
        formatter: (cell) => selectFormat("sec", cell && cell.value),
        formatExtraData: { type: "sec" },
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,

        // numeral(value).format(format ? format : "0a") + " sec";
      },
      {
        dataField: "engagements",
        text: "Engagements",
        style: { width: "120px", minWidth: "120px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "paid",
        text: "Paid",
        style: { width: "90px", minWidth: "90px" },
        formatter: getMetricForColumn,
        formatExtraData: { type: "percentage" },
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0.00%"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "post_time",
        text: "Post Time",
        hidden: true,
      },
      {
        dataField: "url",
        text: "URL",
        hidden: true,
      },
      {
        dataField: "shares",
        text: "Shares",
        style: { width: "90px", minWidth: "90px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "comments",
        text: "Comments",
        style: { width: "90px", minWidth: "90px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "like_total",
        text: "Likes",
        style: { width: "90px", minWidth: "90px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "wow_total",
        text: "WoWs",
        style: { width: "90px", minWidth: "90px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "love_total",
        text: "Loves",
        style: { width: "90px", minWidth: "90px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "sorry_total",
        text: "Sorry",
        style: { width: "90px", minWidth: "90px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "haha_total",
        text: "Haha",
        style: { width: "90px", minWidth: "90px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "anger_total",
        text: "Anger",
        style: { width: "90px", minWidth: "90px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "total_time_live",
        text: "Total Time Live",
        style: { width: "90px", minWidth: "90px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },
      {
        dataField: "views_live",
        text: "Views Live",
        style: { width: "90px", minWidth: "90px" },
        formatter: getMetricForColumn,
        csvFormatter: (cell) =>
          selectFormat("csv_number", cell && cell.value, "0,0"),
        sort: true,
        sortFunc: sortNumeric,
      },
    ];

    let selected_columns = columns;
    if (cells && cells.length) {
      selected_columns = [];
      cells.forEach((cell, i) => {
        selected_columns.push(columns.find((o) => o.dataField === cell));
      });
    }

    // const defaultSorted = [
    //   {
    //     dataField: "impressions",
    //     order: "desc",
    //   },
    // ];

    if (cells && cells.length) {
      selected_columns = [];
      cells.forEach((cell, i) => {
        selected_columns.push(columns.find((o) => o.dataField === cell));
      });
    }

    const paginate = paginationFactory({
      sizePerPage: 5,
      hideSizePerPage: true,
    });

    const filename = title.split(" ").join("_") || "spreadsheet";

    return (
      <ToolkitProvider
        keyField="id"
        data={data}
        columns={selected_columns}
        striped={false}
        hover={false}
        bordered={false}
        exportCSV={{
          fileName: filename + ".csv",
          exportAll: false,
          onlyExportFiltered: true,
        }}
      >
        {(props) => (
          <BootstrapTable
            {...props.baseProps}
            {...props.csvProps}
            pagination={paginate}
            // defaultSorted={defaultSorted}
            filter={filterFactory()}
            ref={(el) => (this.childRef = el)}
          />
        )}
      </ToolkitProvider>
    );
  }

  builtInExportCSV = () => this.childRef;

  render() {
    const { data } = this.props;
    if (data.hasData === false) {
      return <Error minHeight={560} />;
    }

    return this.renderTable(data.data);
  }
}

export default FacebookReview;
